/**
 * Types for the backoffice location-management API — see
 * `docs/location-tracking/location-management.md`. Endpoint sits at
 * `/console/location` and covers cascade browse, text search, per-id
 * lookup, and (ADMIN-only) custom-location writes.
 */

/** A row from `GET /console/location/:district/:subdistrict`. */
export interface CascadeLocation {
  location_name_in_english: string;
  /** `V_<n>` for master rows, `C_<n>` for custom-created rows. */
  id: string;
  pincode: number | null;
}

/**
 * A row from `GET /console/location/search`. The doc lists the
 * headline fields; latitude/longitude aren't in the documented
 * response but the backend may include them, so they're accepted
 * (and treated as optional) so the grid can render them when present.
 */
export interface LocationSearchItem {
  id: string;
  location_name_in_english: string;
  location_alias_name: string | null;
  location_name_in_local: string | null;
  subdistrict_name_in_english: string;
  district_name_in_english: string;
  state_name_in_english: string;
  pincode: number | null;
  /**
   * `0` means custom (id starts with `C_`); any other value is the
   * upstream LGD code for a master-lookup row.
   */
  location_code: number;
  /**
   * Semicolon-joined `"lat;lng"` string (WGS84, decimal degrees).
   * This is the canonical coordinate carrier on both `/search` and
   * `/village/:id` responses — parse with `parseGeoCode`. May be
   * `null`, empty, or missing entirely for master rows that were
   * digitised without coordinates.
   */
  geo_code?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

/** Envelope shape for `GET /console/location/search`. */
export interface LocationSearchResponse {
  list: LocationSearchItem[];
  total: number;
  page: number;
  page_size: number;
}

/** Query params for `GET /console/location/search`. Backend max page_size is 100. */
export interface LocationSearchParams {
  q?: string;
  district?: string;
  subdistrict?: string;
  page?: number;
  page_size?: number;
}

/**
 * A full row from `GET /console/location/village/:lgd_code` —
 * "the full `master_location_lookup` row". The doc doesn't schema
 * every column, so this extends the search shape with the extras
 * we've observed on the wire (lat/lng and a few audit-ish fields).
 * Unknown extras are preserved via the index signature so callers
 * can surface them without a type bump every time the backend
 * adds a column.
 */
export interface MasterLocation extends LocationSearchItem {
  block_name_in_english?: string | null;
  village_code?: number | null;
  altitude?: number | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  [extra: string]: unknown;
}

/** Response envelope for `POST /console/location/customLocation/...`. */
export interface CreateCustomLocationResponse {
  message: string;
  id: string;
}

/** Response envelope for `GET /console/location/cache`. */
export interface RefreshCacheResponse {
  message: string;
}

/**
 * True when the id belongs to a custom-created location — the doc
 * calls out `C_<n>` for customs and `V_<n>` for master rows, and
 * also notes `location_code === 0` on customs. We check the prefix
 * because id-shape is stable while `location_code` isn't returned
 * everywhere the flag matters (e.g. cascade browse omits it).
 */
export const isCustomLocationId = (id: string | undefined | null): boolean =>
  typeof id === 'string' && id.toUpperCase().startsWith('C_');

/**
 * Parse the backend's `geo_code` field into `{ latitude, longitude }`
 * as numbers. The wire format is `"lat;lng"` (semicolon-separated,
 * decimal degrees, WGS84 — e.g. `"31.9380722;75.5706084"`).
 *
 * Any of these produce `{ latitude: null, longitude: null }`:
 *   - `null`, `undefined`, `""`, or non-strings
 *   - a string without exactly two parts after splitting on `;`
 *   - a part that doesn't parse as a finite number
 *   - out-of-range coordinates (|lat| > 90 or |lng| > 180) — likely
 *     malformed data the UI shouldn't confidently render
 *
 * Whitespace around either part is tolerated so `" 31.93 ; 75.57 "`
 * still parses cleanly.
 */
export const parseGeoCode = (
  geoCode: string | null | undefined,
): { latitude: number | null; longitude: number | null } => {
  if (typeof geoCode !== 'string' || geoCode.trim() === '') {
    return { latitude: null, longitude: null };
  }
  const parts = geoCode.split(';').map((p) => p.trim());
  if (parts.length !== 2 || parts[0] === '' || parts[1] === '') {
    // Explicit empty-part check — `Number('')` is `0`, so without
    // this a `";"` payload would sail through as (0, 0) which is
    // a real coordinate off the coast of Africa. Almost certainly
    // not what the master row meant.
    return { latitude: null, longitude: null };
  }
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { latitude: null, longitude: null };
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return { latitude: null, longitude: null };
  }
  return { latitude: lat, longitude: lng };
};
